import { useState } from 'react';
import { Trash2, Loader2 } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { uploadToS3 } from '@/utils/s3presigned';
import { toast } from 'sonner';

const AuthorizationStep = ({ onSubmit }) => {
  const [subStep, setSubStep] = useState(2);
  const [role, setRole] = useState('');
  const [form, setForm] = useState({
    licenseHolderName: '',
    contractorLicense: '',
    fullName: '',
    propertyAddress: '',
    governmentId: null,
    governmentIdKey: null,
    certLegal: false,
    certUnderstanding: false,
    companyName: null,
    mobileNumber: null,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const set = field => e => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const toggle = field => () => setForm(prev => ({ ...prev, [field]: !prev[field] }));


  const validateMobileNumber = (mobileNumber) => {
    const regex = /^\d{10}$/;
    return regex.test(mobileNumber);
  };

  const handleFileUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const { data, error } = await uploadToS3(file, 'authorization-documents');
      if (error) {
        toast.error('Failed to upload file. Please try again.');
        return;
      }
      setForm(prev => ({ ...prev, governmentId: file, governmentIdKey: data.key }));
    } catch {
      toast.error('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const canSubmit =
    role === 'contractor'
      ? form.licenseHolderName && form.contractorLicense && form.certLegal && form.certUnderstanding
      : form.fullName && form.propertyAddress && form.governmentIdKey && form.certLegal && form.certUnderstanding;

  const handleSubmit = async () => {
    if (form.mobileNumber !== null || form.mobileNumber !== '') {
      const isValid = validateMobileNumber(form.mobileNumber);
      if (!isValid) {
        toast.error('Invalid mobile number. Please enter a 10 digit mobile number.');
        return;
      }
    }
    const payload =
      role === 'contractor'
        ? {
          user_type: role,
          license_number: form.contractorLicense,
          license_holder_name: form.licenseHolderName,
          owner_name: 'N/A',
          property_address: 'N/A',
          government_id: 'N/A',
          company_name: form.companyName,
          mobile_number: form.mobileNumber && form.mobileNumber !== '' ? form.mobileNumber : null,
        }
        : {
          user_type: role,
          owner_name: form.fullName,
          property_address: form.propertyAddress,
          government_id: form.governmentIdKey,
          license_number: 'N/A',
          license_holder_name: 'N/A',
          mobile_number: form.mobileNumber && form.mobileNumber !== '' ? form.mobileNumber : null,
        };

    setIsSubmitting(true);
    try {
      await appAxios.post('/Authorization/v1.0/add-authorization', payload);
      toast.success('Authorization submitted successfully');
      onSubmit({ role });
    } catch {
      toast.error('Failed to submit authorization. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls =
    'w-full bg-black border border-gray-700 text-white px-4 py-3 rounded-lg placeholder-gray-500 text-sm focus:outline-none focus:border-gray-500';
  const primaryBtn =
    'w-full bg-[#7B7B7B] hover:bg-gray-500 text-white py-3 rounded-lg transition text-sm disabled:opacity-50 disabled:cursor-not-allowed';
  const outlineBtn =
    'w-full border border-gray-700 hover:border-gray-500 text-white py-3 rounded-lg transition text-sm';

  // Sub-step 2: Role selection
  if (subStep === 2) {
    return (
      <div className="flex flex-col items-center gap-3 w-full">
        <p className="text-gray-400 text-center text-sm max-w-xs">
          For security purposes, please verify your identity to continue.
        </p>
        <p className="text-gray-400 text-center text-sm mb-1">
          Please select who is completing the permit submission.
        </p>
        <button
          onClick={() => setRole('home_owner')}
          className={`w-full py-3 rounded-lg transition text-sm ${role === 'home_owner'
            ? 'bg-white text-black font-medium'
            : 'bg-black border border-gray-700 text-white hover:border-gray-500'
            }`}
        >
          I am a Homeowner
        </button>
        <button
          onClick={() => setRole('contractor')}
          className={`w-full py-3 rounded-lg transition text-sm ${role === 'contractor'
            ? 'bg-white text-black font-medium'
            : 'bg-black border border-gray-700 text-white hover:border-gray-500'
            }`}
        >
          I am a Contractor
        </button>
        <button onClick={() => setSubStep(3)} disabled={!role} className={primaryBtn + ' mt-1'}>
          Continue
        </button>
      </div>
    );
  }

  // Sub-step 3: Details form
  return (
    <div className="flex flex-col gap-3 w-full">
      {role === 'contractor' ? (
        <>
          <input
            type="text"
            placeholder="License Holder Name"
            value={form.licenseHolderName}
            onChange={set('licenseHolderName')}
            className={inputCls}
          />
          <input
            type="text"
            placeholder="Contractor's License #"
            value={form.contractorLicense}
            onChange={set('contractorLicense')}
            className={inputCls}
          />
          <input
            type="text"
            placeholder="Company Name (Optional)"
            value={form.companyName}
            onChange={set('companyName')}
            className={inputCls}
          />
          <input
            type="text"
            placeholder="Mobile Number (Optional)"
            value={form.mobileNumber}
            onChange={set('mobileNumber')}
            className={inputCls}
          />
          <div className="mt-1 space-y-3">
            <label className="flex items-start gap-3 text-sm text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={form.certLegal}
                onChange={toggle('certLegal')}
                className="mt-0.5 w-4 h-4 shrink-0"
              />
              <span>
                I certify that I am a licensed contractor and have been authorized by the property owner to submit
                permit applications, updates, or revisions through Zermit.ai for the project listed.
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={form.certUnderstanding}
                onChange={toggle('certUnderstanding')}
                className="mt-0.5 w-4 h-4 shrink-0"
              />
              <span>
                I understand that the City will issue all permits directly, and Zermit.ai is acting solely as a
                submission facilitator.
              </span>
            </label>
          </div>
        </>
      ) : (
        <>
          <input type="text" placeholder="Full Name" value={form.fullName} onChange={set('fullName')} className={inputCls} />
          <input
            type="text"
            placeholder="Property Address"
            value={form.propertyAddress}
            onChange={set('propertyAddress')}
            className={inputCls}
          />
          <input
            type="text"
            placeholder="Mobile Number (Optional)"
            value={form.mobileNumber}
            onChange={set('mobileNumber')}
            className={inputCls}
          />
          {!form.governmentId ? (
            <div className="flex items-center gap-3">
              <label
                htmlFor="gov-id-upload"
                className={`w-12 h-12 shrink-0 bg-black border border-gray-700 rounded-lg flex items-center justify-center text-white text-2xl ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-500'
                  }`}
              >
                {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : '+'}
              </label>
              <div>
                <p className="text-white text-sm">Upload a valid Government-Issued ID</p>
                <p className="text-xs text-gray-500">Driver&apos;s License, State ID, Passport, or any official photo ID</p>
              </div>
              <input
                id="gov-id-upload"
                type="file"
                onChange={handleFileUpload}
                className="hidden"
                accept="image/*,.pdf"
                disabled={isUploading}
              />
            </div>
          ) : (
            <div className="flex items-center justify-between bg-black border border-gray-700 rounded-lg p-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-green-500">✓</span>
                <span className="text-white text-sm truncate">{form.governmentId.name}</span>
              </div>
              <button
                onClick={() => setForm(prev => ({ ...prev, governmentId: null, governmentIdKey: null }))}
                className="text-red-500 hover:text-red-400 shrink-0 ml-2"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="mt-1 space-y-3">
            <label className="flex items-start gap-3 text-sm text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={form.certLegal}
                onChange={toggle('certLegal')}
                className="mt-0.5 w-4 h-4 shrink-0"
              />
              <span>
                I certify that I am the legal property owner of this address and authorize Zermit.ai to prepare,
                process, and submit this permit application or resubmission on my behalf.
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={form.certUnderstanding}
                onChange={toggle('certUnderstanding')}
                className="mt-0.5 w-4 h-4 shrink-0"
              />
              <span>
                I acknowledge that all permits are issued exclusively by the City, and Zermit.ai&apos;s role is to
                assist with preparation and submission.
              </span>
            </label>
          </div>
        </>
      )}
      <button onClick={handleSubmit} disabled={!canSubmit || isSubmitting} className={primaryBtn + ' mt-1 flex items-center justify-center gap-2'}>
        {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {isSubmitting ? 'Submitting…' : 'Continue'}
      </button>
      <button onClick={() => setSubStep(2)} className={outlineBtn}>
        Back
      </button>
    </div>
  );
};

export default AuthorizationStep;
